import {
  isGlobalManage,
  isWildcardKey,
  permissionsByResource,
  type CatalogPermissionKey,
} from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import {
  permissionDescriptionKey,
  permissionLabelKey,
  permissionResourceLabelKey,
} from '@/shared/i18n/permissions/permissions.util';

export interface PermissionGroupItem {
  key: CatalogPermissionKey;
  /** Translated. */
  label: string;
  /** Translated. */
  description: string;
  isWildcard: boolean;
  isGlobalManage: boolean;
  /**
   * The api would refuse this key in a role or a direct **grant** (N28
   * containment; the global wildcard is a super admin's alone).
   *
   * A **deny** is not refused — it only shrinks a set the actor was already
   * confirmed to contain, which is the asymmetry `UserAccessService` encodes.
   */
  isRefusedToGrant: boolean;
}

export interface PermissionGroup {
  /** `''` for the global wildcard, which belongs to no resource. */
  resource: string;
  /** DOM-safe, stable — the global group's empty resource cannot be an id. */
  id: string;
  /** Translated resource heading. */
  label: string;
  items: readonly PermissionGroupItem[];
  /** The subset a select-all may touch. */
  grantableKeys: readonly CatalogPermissionKey[];
}

/**
 * The permission catalog, grouped by resource and translated — the shape both
 * access editors render.
 *
 * One source for the grouping, deliberately: `permissionsByResource()` in
 * contracts already derives `integrations.<capability>.manage` from the
 * integrations catalog (N21), so a new capability appears in both editors with no
 * edit here. Extracted in P10 because the role checklist and the per-user
 * grant/deny editor are different widgets — a checkbox list and a tri-state — over
 * the *same* grouping, and duplicating the grouping is how the two would drift.
 *
 * Labels are resolved here rather than in the components, because the dotted key
 * lookup is the one that broke in P9 (`key.billing\.manage.label` rendered raw)
 * and it should have exactly one call site per kind.
 */
export function usePermissionGroups(): readonly PermissionGroup[] {
  const { t } = useTranslation('permissions');
  const { mayGrant } = useHasPermission();

  return useMemo(() => {
    const groups: PermissionGroup[] = [];

    for (const [resource, definitions] of permissionsByResource().entries()) {
      const items = definitions.map<PermissionGroupItem>((definition) => {
        const key = definition.key as CatalogPermissionKey;

        return {
          key,
          label: t(permissionLabelKey(key)),
          description: t(permissionDescriptionKey(key)),
          isWildcard: isWildcardKey(key),
          isGlobalManage: isGlobalManage(key),
          isRefusedToGrant: !mayGrant(key),
        };
      });

      groups.push({
        resource,
        id: resource === '' ? 'global' : resource,
        label: t(permissionResourceLabelKey(resource)),
        items,
        grantableKeys: items.filter((item) => !item.isRefusedToGrant).map((item) => item.key),
      });
    }

    return groups;
  }, [mayGrant, t]);
}
